import { IGenreSvodEpochOffers } from 'lib/types/global';

/**
 * Epoch tokens arrive in several namespaces with inconsistent punctuation and
 * case, so every lookup goes through one canonical form. Exported so the grid's
 * chip rendering normalizes identically — two implementations would drift.
 */
export const normalizeGenreKey = (key: any): string => `${key ?? ''}`.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

interface BuildGenreConflictGraphArgs {
  getOffersGenreData?: Partial<IGenreSvodEpochOffers>;
  packagesConfig?: Array<any>;
}

/**
 * Epoch-derived genre conflict graph: pack id → the pack ids it cannot be
 * selected alongside. Verified against the live feed, this yields exactly the
 * intended behaviour:
 *
 *   Duo MyEntertainment+MyNews ⟂ MyEntertainment, MyNews, Duo MySports+MyNews
 *   Duo MySports+MyNews        ⟂ MySports,        MyNews, Duo MyEntertainment+MyNews
 *
 * Built from four sources, because no single one carries the whole picture:
 *
 *   1. `getOffersGenreData.offerPrices`  — pack id → `code` / `billingCode`.
 *      A pack has MULTIPLE offers (promo variants), each with its own code, so
 *      every one is indexed; a conflict may name any of them.
 *   2. `packagesConfig`                  — `id` / `epochPackageName` /
 *      `displayLabel` / `queryparam` / `billingProductCode`, covering packs
 *      with no priced offer. These are 1:1 with a pack, so they are indexed
 *      ahead of the product namespace below.
 *   3. `getOffersGenreData.svodMapping`  — pack id → its Epoch PRODUCT keys.
 *      The resolver's conflict fallback emits product-namespace keys
 *      (epoch-genre-offers.resolver.ts conflictKeysFor), which neither of the
 *      other sources can resolve. Indexed LAST and only when unambiguous —
 *      see the comment on `packageIdsByProductToken`.
 *   4. `getOffersGenreData.conflictingProducts` — the edges themselves, as raw
 *      Epoch tokens needing (1)–(3) to become pack ids.
 *
 * Edges are SYMMETRISED. Epoch happens to author both directions for the Duos
 * today, but it authors against whichever end the modeller picked, so relying
 * on that would silently drop half the conflicts for any future pack.
 */
export const buildGenreConflictGraph = ({
  getOffersGenreData,
  packagesConfig
}: BuildGenreConflictGraphArgs): Record<string, string[]> => {
  const packageIdByToken: Record<string, string> = {};
  const indexToken = (token: any, packageId: string) => {
    const key = normalizeGenreKey(token);
    // First writer wins: the namespaces that identify a pack 1:1 are indexed
    // before the shared product namespace, so a live Epoch offer code is never
    // shadowed by a weaker alias.
    if (key && packageId && !packageIdByToken[key]) {
      packageIdByToken[key] = packageId;
    }
  };

  /**
   * Pack ids whose conflicts this graph must not carry. Eviction only rewrites
   * `columnSelections[col].genre`; mini packs live in `miniSelectionsByColumn`
   * and `handleMiniPackChipClick` never consults the graph. An edge on a mini
   * would therefore evict nothing AND fire no snackbar, letting the invalid
   * combination reach `getPriceOffers` and the signup href unnoticed — worse
   * than never building it. Enforcing minis is a behaviour change that needs
   * its own AC, so for now they are excluded at the source.
   *
   * Only packs positively KNOWN to be minis are dropped: an id absent from
   * `packagesConfig` stays enforceable, since the offer-code path resolves
   * plenty of packs this prop never lists.
   */
  const unenforceableIds = new Set(
    (packagesConfig || [])
      .filter((pkg: any) => `${pkg?.type || ''}`.startsWith('genre-mini'))
      .map((pkg: any) => pkg?.id)
      .filter(Boolean)
  );

  // `code` (promotion code) and `billingCode` (rotates with promos) share one
  // flat index with the aliases below. That assumes these Epoch namespaces
  // never normalize to the same token across DIFFERENT packs — if they ever did,
  // first-writer-wins would bind the token to whichever pack was indexed first
  // and produce a spurious conflict edge. Holds today; revisit if the code
  // formats change.
  (getOffersGenreData?.offerPrices || []).forEach(obj => {
    Object.entries(obj || {}).forEach(([packageId, val]) => {
      indexToken(packageId, packageId);
      indexToken(val?.code, packageId);
      indexToken(val?.billingCode, packageId);
    });
  });

  (packagesConfig || [])
    .filter((pkg: any) => `${pkg?.type || ''}`.startsWith('genre'))
    .forEach((pkg: any) => {
      // `billingProductCode` is the canonical product-code join used elsewhere
      // (channels-service buildBillingProductCodeMap), so a product-level
      // conflict key can land on it rather than on an Epoch product key.
      [pkg?.id, pkg?.epochPackageName, pkg?.displayLabel, pkg?.queryparam, pkg?.billingProductCode]
        .forEach(token => indexToken(token, pkg?.id));
    });

  /**
   * Product namespace, collected before indexing because it is NOT 1:1.
   * `svodMapping` is pack id → the SVOD product codes the pack INCLUDES, so
   * read in reverse one code names MANY packs — a Duo and its parent list the
   * same apps (utils/getSVODS.ts pushes every pack whose list contains a code).
   * Folding that into the flat first-writer-wins index would bind a shared code
   * to whichever pack Epoch happened to return first, which on identical
   * payloads either fabricates an edge to the wrong pack (a pack the agent
   * chose is silently evicted and the snackbar names it) or binds to the pack
   * that owns the conflict entry, hits the `a === b` guard and drops the real
   * conflict. Only an unambiguous code identifies a pack.
   */
  const packageIdsByProductToken: Record<string, Set<string>> = {};
  Object.entries(getOffersGenreData?.svodMapping || {}).forEach(([packageId, productKeys]) => {
    (Array.isArray(productKeys) ? productKeys : []).forEach(key => {
      const token = normalizeGenreKey(key);
      if (token && packageId) {
        (packageIdsByProductToken[token] = packageIdsByProductToken[token] || new Set()).add(packageId);
      }
    });
  });
  Object.entries(packageIdsByProductToken).forEach(([token, packageIds]) => {
    if (packageIds.size === 1) {
      indexToken(token, [...packageIds][0]);
    }
  });

  const graph: Record<string, Set<string>> = {};
  const addEdge = (a: string, b: string) => {
    if (!a || !b || a === b) {
      return;
    }
    if (unenforceableIds.has(a) || unenforceableIds.has(b)) {
      return;
    }
    (graph[a] = graph[a] || new Set()).add(b);
    (graph[b] = graph[b] || new Set()).add(a);
  };

  Object.entries(getOffersGenreData?.conflictingProducts || {}).forEach(([packageId, tokens]) => {
    (Array.isArray(tokens) ? tokens : []).forEach(token => {
      // Tokens naming something outside the genre catalogue don't resolve and
      // are skipped — an unmodelled pack simply has no conflicts.
      addEdge(packageId, packageIdByToken[normalizeGenreKey(token)]);
    });
  });

  return Object.fromEntries(Object.entries(graph).map(([id, set]) => [id, [...set]]));
};
